import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AppPermissionWrapper from './components/AppPermissionWrapper';
import RoleSelectionScreen from './components/screens/RoleSelectionScreen';
import TeacherDashboardScreen from './components/screens/TeacherDashboardScreen';
import LearningPathDesignerScreen from './components/screens/LearningPathDesignerScreen';
import TeacherLearningPathDesignerScreen from './components/screens/TeacherLearningPathDesignerScreen';
import LearningPathManageScreen from './components/screens/LearningPathManageScreen';
import LearningPathAssignmentScreen from './components/screens/LearningPathAssignmentScreen';
import MainLayout from './components/layouts/MainLayout';
import { mockTeachers } from './data/mockTeachers';
import { mockClasses } from './data/mockClasses';
import { UserRole } from './data/types';
import StudentManagementScreen from './components/screens/StudentManagementScreen';
import ReportsScreen from './components/screens/ReportsScreen';
import ClassDetailsScreen from './components/screens/ClassDetailsScreen';
import ClassesScreen from './components/screens/ClassesScreen';
import StandardsScreen from './components/screens/StandardsScreen';
import StandardDetailScreen from './components/screens/StandardDetailScreen';
import TeacherGameLibraryScreen from './components/screens/TeacherGameLibraryScreen';
import ResourcesScreen from './components/screens/ResourcesScreen';
import StudentGroupsScreen from './components/screens/StudentGroupsScreen';
import UserProfileScreen from './components/screens/UserProfileScreen';
import UserSettingsScreen from './components/screens/UserSettingsScreen';

// Super Admin Screens
import SuperAdminDashboardScreen from './components/screens/SuperAdminDashboardScreen';
import SuperAdminDistrictManagementScreen from './components/screens/SuperAdminDistrictManagementScreen';
import SuperAdminSchoolManagementScreen from './components/screens/SuperAdminSchoolManagementScreen';
import AdminDashboardScreen from './components/screens/AdminDashboardScreen';
import SuperAdminSystemConfigScreen from './components/screens/SuperAdminSystemConfigScreen';
import SuperAdminGameManagementScreen from './components/screens/SuperAdminGameManagementScreen';
import SuperAdminQuestionManagementScreen from './components/screens/SuperAdminQuestionManagementScreen';
import QuestionEditScreen from './components/screens/QuestionEditScreen';
import QuestionViewScreen from './components/screens/QuestionViewScreen';
import SuperAdminAnalyticsScreen from './components/screens/SuperAdminAnalyticsScreen';
import SuperAdminUserManagementScreen from './components/screens/SuperAdminUserManagementScreen';
import StudentGroupDetailScreen from './components/screens/StudentGroupDetailScreen';
import StudentGroupFormScreen from './components/screens/StudentGroupFormScreen';
import { ThemeProvider, PageTransition } from './components/ThemeProvider';
import SuperAdminLearningPathManageScreen from './components/screens/SuperAdminLearningPathManageScreen';
import SuperAdminLearningPathDesignerScreen from './components/screens/SuperAdminLearningPathDesignerScreen';

// Student Screens
import StudentLogin from './components/screens/students/StudentLogin';
import StudentDashboard from './components/screens/students/StudentDashboard';
import StudentDashboardV2 from './components/screens/students/StudentDashboardV2';
import StudentLearningPaths from './components/screens/students/StudentLearningPaths';
import LearningPhase from './components/screens/students/LearningPhase';
import LearningPhaseScreen from './components/screens/students/LearningPhaseScreen';
import GameGrid from './components/screens/students/GameGrid';
import GamePlay from './components/screens/students/GamePlay';
import Leaderboard from './components/screens/students/Leaderboard';
import StudentProgress from './components/screens/students/StudentProgress';
import StudentLayoutV3 from './components/layouts/StudentLayoutV3';
import StudentDashboardV3 from './components/screens/students/StudentDashboardV3';
import StudentLayoutV4 from './components/layouts/StudentLayoutV4';
import StudentDashboardV4 from './components/screens/students/StudentDashboardV4';

// Auth Context to manage authentication state
export const AuthContext = React.createContext<{
  activeRole: UserRole | null;
  setActiveRole: React.Dispatch<React.SetStateAction<UserRole | null>>;
  logout: () => void;
}>({
  activeRole: null,
  setActiveRole: () => {},
  logout: () => {},
});

// Auth Provider component
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state from localStorage if available
  const [activeRole, setActiveRoleState] = useState<UserRole | null>(() => {
    const savedRole = localStorage.getItem('activeRole');
    return savedRole ? savedRole as UserRole : null;
  });
  
  // Custom setter to update both state and localStorage
  const setActiveRole = (role: React.SetStateAction<UserRole | null>) => {
    setActiveRoleState(prevRole => {
      const newRole = typeof role === 'function' ? role(prevRole) : role;
      // Update localStorage
      if (newRole) {
        localStorage.setItem('activeRole', newRole);
      } else {
        localStorage.removeItem('activeRole');
      }
      
      // Update the dataService with the new role to clear cached data
      const { dataService } = require('./services/dataService');
      dataService.setActiveRole(newRole);
      
      return newRole;
    });
  };
  
  // Logout function to reset active role
  const logout = () => {
    setActiveRole(null);
    
    // Clear dataService cache
    const { dataService } = require('./services/dataService');
    dataService.setActiveRole(null);
  };
  
  return (
    <AuthContext.Provider value={{ activeRole, setActiveRole, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route component
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  requiredRole?: UserRole;
}> = ({ children, requiredRole }) => {
  const { activeRole } = React.useContext(AuthContext);
  const location = useLocation();
  
  // Check if we're in the student section and set a persistent flag
  useEffect(() => {
    if (location.pathname.includes('/student/')) {
      localStorage.setItem('isStudentSession', 'true');
    }
  }, [location.pathname]);
  
  // For student routes, check if we have a student session
  const isStudentPath = location.pathname.includes('/student/');
  const hasStudentSession = localStorage.getItem('isStudentSession') === 'true';
  const isLearningPath = location.pathname.includes('/student/learning/');
  
  // Special case for student routes:
  // 1. If it's a student path and we have a student session flag set, allow access
  // 2. If it's specifically a learning path, always allow access and let the component handle auth
  if ((isStudentPath && hasStudentSession && requiredRole === 'Student') || isLearningPath) {
    return <>{children}</>;
  }
  
  // For non-student routes or student routes without session:
  // If no active role, redirect to role selection
  if (!activeRole) {
    // Store the current location to redirect back after login
    sessionStorage.setItem('redirectPath', location.pathname);
    return <Navigate to="/" replace />;
  }
  
  // If role doesn't match required role, redirect to appropriate dashboard
  if (requiredRole && activeRole !== requiredRole) {
    const redirectPath = 
      activeRole === 'SuperAdmin' ? '/superadmin/dashboard' : 
      activeRole === 'SchoolAdmin' ? '/schooladmin/dashboard' : 
      '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }
  
  return <>{children}</>;
};

// AnimatedRoutes component to handle page transitions
function AnimatedRoutes() {
  const { activeRole, setActiveRole } = React.useContext(AuthContext);
  const location = useLocation();
  
  const handleRoleSelect = (role: UserRole) => {
    console.log('handleRoleSelect called with role:', role);
    
    // Import dataService and set the active role
    const { dataService } = require('./services/dataService');
    dataService.setActiveRole(role);
    
    // Clear any cached state
    sessionStorage.removeItem('lastViewedPage');
    
    // Update auth context
    setActiveRole(role);
    
    // Always clear redirectPath when switching roles
    sessionStorage.removeItem('redirectPath');
    // Navigate to the default dashboard for the selected role
    if (role === 'SuperAdmin') {
      window.location.href = '/superadmin/dashboard';
    } else if (role === 'SchoolAdmin') {
      window.location.href = '/schooladmin/dashboard';
    } else if (role === 'Teacher') {
      window.location.href = '/dashboard';
    } else if (role === 'Student') {
      window.location.href = '/student/login';
    }
  };
  
  return (
    <PageTransition location={location.pathname}>
      <Routes>
        <Route
          path="/"
          element={
            activeRole ? (
              <Navigate to={
                activeRole === 'SuperAdmin' ? '/superadmin/dashboard' : 
                activeRole === 'SchoolAdmin' ? '/schooladmin/dashboard' : 
                activeRole === 'Student' ? '/student/login' :
                '/dashboard'
              } replace />
            ) : (
              <RoleSelectionScreen onRoleSelect={handleRoleSelect} />
            )
          }
        />
        
        {/* Dashboard route - conditionally render based on role */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              {activeRole === 'SuperAdmin' ? (
                <MainLayout 
                  userName="Super Admin"
                  userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                  userRole="SuperAdmin"
                >
                  <SuperAdminDashboardScreen />
                </MainLayout>
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <TeacherDashboardScreen teacher={mockTeachers[0]} classes={mockClasses} />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        {/* Super Admin Routes */}
        <Route
          path="/districts"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <SuperAdminDashboardScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schools"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminSchoolManagementScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/system-config"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminSystemConfigScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/questions"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminQuestionManagementScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/questions/:questionId/edit"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <QuestionEditScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/analytics"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminAnalyticsScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/users"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminUserManagementScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/games"
          element={
            <ProtectedRoute>
              {activeRole === 'SuperAdmin' ? (
                <MainLayout 
                  userName="Super Admin"
                  userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                  userRole="SuperAdmin"
                >
                  <AppPermissionWrapper userRole="SuperAdmin">
                    <SuperAdminGameManagementScreen />
                  </AppPermissionWrapper>
                </MainLayout>
              ) : activeRole === 'SchoolAdmin' ? (
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <MainLayout 
                    userName="School Admin"
                    userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                    userRole="SchoolAdmin"
                  >
                    <SuperAdminGameManagementScreen />
                  </MainLayout>
                </AppPermissionWrapper>
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <TeacherGameLibraryScreen />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/learning-paths/create"
          element={
            <ProtectedRoute>
              {activeRole === 'SchoolAdmin' ? (
                <Navigate to="/schooladmin/learning-paths/create" replace />
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <TeacherLearningPathDesignerScreen />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/learning-paths/edit/:pathId"
          element={
            <ProtectedRoute>
              {activeRole === 'SchoolAdmin' ? (
                <Navigate to="/schooladmin/learning-paths" replace />
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <TeacherLearningPathDesignerScreen />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/learning-paths/manage"
          element={
            <ProtectedRoute>
              {activeRole === 'SuperAdmin' ? (
                <Navigate to="/superadmin/learning-paths" replace />
              ) : activeRole === 'SchoolAdmin' ? (
                <Navigate to="/schooladmin/learning-paths" replace />
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <LearningPathManageScreen />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/learning-paths/assign"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <LearningPathAssignmentScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/students"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <StudentManagementScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <ReportsScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/classes"
          element={
            <ProtectedRoute>
              {activeRole === 'SuperAdmin' ? (
                <MainLayout 
                  userName="Super Admin"
                  userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                  userRole="SuperAdmin"
                >
                  <ClassesScreen />
                </MainLayout>
              ) : activeRole === 'SchoolAdmin' ? (
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <MainLayout 
                    userName="School Admin"
                    userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                    userRole="SchoolAdmin"
                  >
                    <ClassesScreen />
                  </MainLayout>
                </AppPermissionWrapper>
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <ClassesScreen />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/classes/:classId"
          element={
            <ProtectedRoute>
              {activeRole === 'SuperAdmin' ? (
                <MainLayout 
                  userName="Super Admin"
                  userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                  userRole="SuperAdmin"
                >
                  <ClassDetailsScreen />
                </MainLayout>
              ) : activeRole === 'SchoolAdmin' ? (
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <MainLayout 
                    userName="School Admin"
                    userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                    userRole="SchoolAdmin"
                  >
                    <ClassDetailsScreen />
                  </MainLayout>
                </AppPermissionWrapper>
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <ClassDetailsScreen />
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/standards"
          element={
            <ProtectedRoute>
              {activeRole === 'SchoolAdmin' ? (
                <Navigate to="/schooladmin/standards" replace />
              ) : activeRole === 'SuperAdmin' ? (
                <MainLayout 
                  userName="Super Admin"
                  userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                  userRole="SuperAdmin"
                >
                  <AppPermissionWrapper userRole="SuperAdmin">
                    <StandardsScreen />
                  </AppPermissionWrapper>
                </MainLayout>
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <AppPermissionWrapper userRole="Teacher">
                    <StandardsScreen />
                  </AppPermissionWrapper>
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schooladmin/standards"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <StandardsScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/standards/:standardId"
          element={
            <ProtectedRoute>
              {activeRole === 'SchoolAdmin' ? (
                <Navigate to="/schooladmin/standards/:standardId" replace />
              ) : activeRole === 'SuperAdmin' ? (
                <MainLayout 
                  userName="Super Admin"
                  userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                  userRole="SuperAdmin"
                >
                  <AppPermissionWrapper userRole="SuperAdmin">
                    <StandardDetailScreen />
                  </AppPermissionWrapper>
                </MainLayout>
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <AppPermissionWrapper userRole="Teacher">
                    <StandardDetailScreen />
                  </AppPermissionWrapper>
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schooladmin/standards/:standardId"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <StandardDetailScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Question View Routes */}
        <Route
          path="/questions/:questionId"
          element={
            <ProtectedRoute>
              {activeRole === 'SuperAdmin' ? (
                <Navigate to="/questions/:questionId/edit" replace />
              ) : (
                <MainLayout 
                  userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                  userAvatar={mockTeachers[0].avatar}
                  userRole="Teacher"
                >
                  <AppPermissionWrapper userRole="Teacher">
                    <QuestionViewScreen />
                  </AppPermissionWrapper>
                </MainLayout>
              )}
            </ProtectedRoute>
          }
        />



        <Route
          path="/resources"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <ResourcesScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/student-groups"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <StudentGroupsScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/student-groups/:groupId"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <StudentGroupDetailScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/student-groups/create"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <StudentGroupFormScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/student-groups/:groupId/edit"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <StudentGroupFormScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/learning-paths/edit/:pathId"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <LearningPathDesignerScreen mode="manage" />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* User Profile and Settings Routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <UserProfileScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout 
                userName={`${mockTeachers[0].firstName} ${mockTeachers[0].lastName}`}
                userAvatar={mockTeachers[0].avatar}
                userRole="Teacher"
              >
                <UserSettingsScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        


        {/* SuperAdmin Routes */}
        <Route
          path="/superadmin/dashboard"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminDashboardScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/districts"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminDistrictManagementScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* SuperAdmin Learning Path Management Routes */}
        <Route
          path="/superadmin/learning-paths"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminLearningPathManageScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/learning-paths/create"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminLearningPathDesignerScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/learning-paths/edit/:pathId"
          element={
            <ProtectedRoute requiredRole="SuperAdmin">
              <MainLayout 
                userName="Super Admin"
                userAvatar="https://placehold.co/100x100/27ae60/ffffff?text=SA"
                userRole="SuperAdmin"
              >
                <AppPermissionWrapper userRole="SuperAdmin">
                  <SuperAdminLearningPathDesignerScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        


        {/* SchoolAdmin Routes */}
        <Route
          path="/schooladmin/dashboard"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <AdminDashboardScreen userRole="SchoolAdmin" schoolId="school-001" />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/schooladmin/classes"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <ClassesScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/schooladmin/games"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminGameManagementScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* My School page removed as per requirements */}
        
        <Route
          path="/schooladmin/questions"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminQuestionManagementScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schooladmin/analytics"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminAnalyticsScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schooladmin/users"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminUserManagementScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schooladmin/system-config"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminSystemConfigScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* SchoolAdmin Learning Path Management Routes */}
        <Route
          path="/schooladmin/learning-paths"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminLearningPathManageScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schooladmin/learning-paths/create"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminLearningPathDesignerScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/schooladmin/learning-paths/edit/:pathId"
          element={
            <ProtectedRoute requiredRole="SchoolAdmin">
              <MainLayout 
                userName="School Admin"
                userAvatar="https://placehold.co/100x100/3498db/ffffff?text=SA"
                userRole="SchoolAdmin"
              >
                <AppPermissionWrapper userRole="SchoolAdmin">
                  <SuperAdminLearningPathDesignerScreen />
                </AppPermissionWrapper>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Student Role Routes */}
        <Route
          path="/student/login"
          element={<StudentLogin />}
        />
        {/* Redirect /student/dashboard to use V4 dashboard */}
        <Route
          path="/student/dashboard"
          element={<Navigate to="/student/dashboardv4" replace />}
        />
        {/* Original dashboard accessible via dashboardv1 */}
        <Route
          path="/student/dashboardv1"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <StudentDashboard /> {/* V1 component */}
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/dashboardv2" // V2 path
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <StudentDashboardV2 /> {/* V2 component */}
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        {/* Student V3 Dashboard Routes */}
        <Route element={<ProtectedRoute requiredRole="Student"><StudentLayoutV3 /></ProtectedRoute>}>
          <Route path="/student/dashboardv3" element={<StudentDashboardV3 />} />
          {/* Future V3 student routes can be nested here */}
        </Route>
        
        {/* Student V4 Dashboard Routes - Gamified Version */}
        <Route element={<ProtectedRoute requiredRole="Student"><StudentLayoutV4 /></ProtectedRoute>}>
          <Route path="/student/dashboardv4" element={<StudentDashboardV4 />} />
          <Route path="/student/learning-v4/:pathId/:standardId" element={<LearningPhase />} />
          {/* Future V4 student routes can be nested here */}
        </Route>
        <Route
          path="/student/learning-paths"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <StudentLearningPaths />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/learning/:pathId/:standardId"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <LearningPhase />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/learning-v2/:pathId/:standardId"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <LearningPhaseScreen />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/games"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <GameGrid />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/games/:standardId"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <GameGrid />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/game/:gameId"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <GamePlay />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/leaderboard"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <Leaderboard />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/progress"
          element={
            <ProtectedRoute requiredRole="Student">
              <AppPermissionWrapper userRole="Student">
                <StudentProgress />
              </AppPermissionWrapper>
            </ProtectedRoute>
          }
        />
        {/* End of Student Routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PageTransition>
  );
}

// Main App component 
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <AnimatedRoutes />
          <ToastContainer position="top-right" autoClose={3000} />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App; 